import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Save } from "lucide-react";
import { api, formatApiErrorDetail, formatPrice, resolveImage } from "@/lib/api";

export default function AdminSpotlight() {
  const [products, setProducts] = useState([]);
  const [setting, setSetting] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/products").then(({ data }) => setProducts(data)),
      api.get("/admin/spotlight").then(({ data }) => setSetting(data)),
    ]).catch(() => setSetting({ enabled: false, product_id: "", label: "À la une" }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/spotlight", setting);
      setSetting(data);
      toast.success(data.enabled ? "Produit mis en avant sur l'accueil" : "Mise en avant désactivée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!setting) {
    return (
      <div className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={26} />
        <span className="font-serif text-lg">Chargement…</span>
      </div>
    );
  }

  const selected = products.find((p) => p.product_id === setting.product_id);

  return (
    <div data-testid="admin-spotlight-page" className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-bark">Produit à la une</h1>
        <p className="mt-1 text-sm text-bark-soft">
          Mettez en avant un produit tout en haut de la page d'accueil, au-dessus du titre. Activez-le uniquement quand vous le souhaitez.
        </p>
      </div>

      <div className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
        {/* Activation */}
        <label className="flex items-center justify-between gap-4">
          <span>
            <span className="block text-sm font-semibold text-bark">Afficher un produit à la une</span>
            <span className="block text-xs text-bark-soft">Une pastille cliquable apparaîtra en haut de l'accueil.</span>
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={setting.enabled}
            data-testid="spotlight-toggle"
            onClick={() => setSetting((s) => ({ ...s, enabled: !s.enabled }))}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${setting.enabled ? "bg-amber-deep" : "bg-stone-300"}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${setting.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </label>

        {/* Sélection produit */}
        <div>
          <label htmlFor="spotlight-product" className="mb-1.5 block text-sm font-medium text-bark">Produit</label>
          <select
            id="spotlight-product"
            data-testid="spotlight-product"
            value={setting.product_id}
            onChange={(e) => setSetting((s) => ({ ...s, product_id: e.target.value }))}
            className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm text-bark outline-none transition-colors focus:border-amber-deep"
          >
            <option value="">— Choisir un produit —</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Libellé */}
        <div>
          <label htmlFor="spotlight-label" className="mb-1.5 block text-sm font-medium text-bark">Libellé du badge</label>
          <input
            id="spotlight-label"
            data-testid="spotlight-label"
            value={setting.label}
            maxLength={40}
            onChange={(e) => setSetting((s) => ({ ...s, label: e.target.value }))}
            placeholder="À la une"
            className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm text-bark outline-none transition-colors focus:border-amber-deep"
          />
        </div>

        {/* Aperçu */}
        {setting.product_id && selected && (
          <div data-testid="spotlight-preview" className="rounded-xl border border-amber-900/10 bg-sand/40 p-4">
            <p className="mb-3 text-xs uppercase tracking-[0.15em] text-stone-mute">Aperçu</p>
            <div className="w-44 overflow-hidden rounded-2xl border border-amber-deep/20 bg-white shadow-sm">
              <div className="relative aspect-[4/3] overflow-hidden bg-sand">
                {selected.image_url ? (
                  <img src={resolveImage(selected.image_url)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-amber-deep"><Megaphone size={26} /></span>
                )}
                <span className="absolute left-2 top-2 rounded-full bg-amber-deep px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow">
                  {setting.label || "À la une"}
                </span>
              </div>
              <div className="px-3 py-2.5">
                <p className="truncate text-sm font-medium text-bark">{selected.name}</p>
                {selected.price_cents > 0 && (
                  <p className="mt-0.5 text-sm font-semibold text-amber-deep">{formatPrice(selected.price_cents)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <button
          data-testid="spotlight-save"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
        </button>
      </div>
    </div>
  );
}
